/**
 * Shared helper functions for guardian/principal forms.
 * Used across Bond Retrieval, Bond Creation Historic, and Features/Bonds components.
 */

/**
 * Determine the current UI mode for a principal based on their selection state.
 * Replaces nested ternary logic with explicit mode determination.
 */
export const getPrincipalMode = (guardian) => {
  if (!guardian) return "manual";
  if (guardian.is_professional_guardian === "Yes" && !guardian.showFullForm) {
    return "pg_lookup";
  }
  if (guardian.is_attorney === "Yes" && !guardian.showAttorneyForm) {
    return "attorney_lookup";
  }
  return "manual";
};

/**
 * Create a guardian patcher function for updating a specific guardian's fields.
 * Reduces repetitive spread/update patterns throughout the forms.
 */
export const createGuardianPatcher = (data, updateData, guardians) => {
  return (index, patch) => {
    const updated = [...guardians];
    updated[index] = { ...updated[index], ...patch };
    updateData({ ...data, guardians: updated });
  };
};

/**
 * Field name mapping for Guardianship forms which use guardian_* prefixed fields.
 * Other bond types use flat field names (name, address, city, etc.).
 */
export const guardianshipFieldMap = {
  name: "guardian_name",
  address: "guardian_street_address_1",
  city: "guardian_city",
  state: "guardian_state",
  zip_code: "guardian_zip",
  occupation: "guardian_occupation",
  relationship: "guardian_relationship_to_ward",
  citizenship: "guardian_citizenship",
  custom_citizenship: "guardian_custom_citizenship",
  country: "guardian_country",
  custom_country: "guardian_custom_country",
  email: "guardian_email",
  ssn: "guardian_ssn",
};

/**
 * Default field map for Probate, VA, and Trustee forms.
 */
export const defaultFieldMap = {
  name: "name",
  address: "address",
  city: "city",
  state: "state",
  zip_code: "zip_code",
  occupation: "occupation",
  relationship: "relationship",
  citizenship: "citizenship",
  custom_citizenship: "custom_citizenship",
  country: "country",
  custom_country: "custom_country",
  email: "email",
  ssn: "ssn",
};

/**
 * Get the appropriate field map based on bond type.
 */
export const getFieldMap = (bondType) => {
  if (bondType === "guardianship") {
    return guardianshipFieldMap;
  }
  return defaultFieldMap;
};

/**
 * Get the value of a field from guardian using the appropriate field map.
 */
export const getFieldValue = (guardian, fieldName, fieldMap) => {
  const key = fieldMap[fieldName] || fieldName;
  return guardian?.[key];
};

/**
 * Create a field update object using the appropriate field map.
 */
export const createFieldUpdate = (fieldName, value, fieldMap) => {
  const key = fieldMap[fieldName] || fieldName;
  return { [key]: value };
};

/**
 * Standard options for citizenship and country dropdowns.
 */
export const CITIZENSHIP_OPTIONS = [
  "U.S. Citizen",
  "Permanent Resident",
  "Non-Resident Alien",
  "Other",
];

export const COUNTRY_OPTIONS = [
  "United States",
  "Canada",
  "Mexico",
  "United Kingdom",
  "Other",
];

/**
 * Check if bond amount qualifies for high-bond fields (SSN, occupation).
 * Standard threshold is $250,000.
 */
export const isHighBondAmount = (bondAmount, threshold = 250000) => {
  const amount = typeof bondAmount === "string" 
    ? Number.parseFloat(bondAmount.replaceAll(/[^0-9.]/g, "")) 
    : bondAmount;
  return !Number.isNaN(amount) && amount >= threshold;
};

/**
 * Format SSN for display (masked).
 */
export const formatSSN = (ssn) => {
  if (!ssn || ssn.length < 4) return ssn || "";
  return "XXX-XX-" + ssn.slice(-4);
};

/**
 * Split employment/occupation combined value into status and text.
 */
export const splitEmploymentOccupation = (combined) => {
  if (!combined) return { status: "", text: "" };
  const parts = combined.split(" - ");
  return {
    status: parts[0] || "",
    text: parts.slice(1).join(" - ") || "",
  };
};

/**
 * Join employment status and occupation text into combined value.
 */
export const joinEmploymentOccupation = (status, text) => {
  if (!status) return "";
  if (!text || status === "Unemployed") return status;
  return `${status} - ${text}`;
};

/**
 * Get occupation question label based on employment status.
 */
export const getOccupationQuestionLabel = (employmentStatus) => {
  if (employmentStatus === "Retired") {
    return "What was your occupation before retirement?";
  }
  return "What is your current occupation?";
};
